import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../api';
import NotificationBell from './NotificationBell';

const ALL_NAV = [
  { id: 'most-liked',      label: 'Most Liked',       icon: '🏆' },
  { id: 'company-heatmap', label: 'Company Heat Map', icon: '🔥' },
  { id: 'jobs',            label: 'Jobs',             icon: '💼' },
  { id: 'salaries',        label: 'Role Salary Guide',icon: '📊' },
  { id: 'top-questions',   label: 'Top Questions',    icon: '❓' },
];

export default function MobileHeader({ onSearch, searchQuery, user, setUser, setView, onReviewClick, onHomeClick, darkMode, toggleDarkMode, view }) {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const inputRef = useRef(null);

  const activeView = view === 'home' || view === 'search' || view === 'highlight' ? 'home' : view;

  // Sync clear from outside
  useEffect(() => {
    if (!searchQuery) setSearchValue('');
  }, [searchQuery]);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [searchOpen]);

  const handleSearch = (val) => {
    setSearchValue(val);
    onSearch && onSearch(val);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchValue('');
    onSearch && onSearch('');
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate('/');
    if (onHomeClick) onHomeClick();
    else if (setView) setView('home');
  };

  const handleSignOut = async () => {
    setMenuOpen(false);
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      setUser && setUser(null);
      navigate('/login');
    }
  };

  const handleNav = (id) => {
    setMenuOpen(false);
    if (id === 'home' && onHomeClick) onHomeClick();
    else setView(id);
  };

  const isGuest = user?.isAnonymous || user?.provider === 'anon';

  return (
    <>
      {/* Fixed Mobile Header Bar */}
      <header className="mobile-header" style={{ padding: '0 12px' }}>

        {/* Hamburger menu button — left side */}
        <button
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Open navigation menu"
          data-tour="nav-mobile"
          style={{
            width: 36, height: 36, borderRadius: 10,
            background: menuOpen ? 'var(--surface2)' : 'transparent',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: menuOpen ? 'var(--blue)' : 'var(--text2)',
            flexShrink: 0, marginRight: 4,
          }}
        >
          {menuOpen ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <line x1="3" y1="6"  x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>

        {/* Logo — taps to home */}
        <a
          href="/"
          onClick={handleLogoClick}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            textDecoration: 'none', flexShrink: 0,
          }}
        >
          <img src="/logo-mascot.png" alt="HireKahani Elephant" style={{ width: 28, height: 28, objectFit: 'contain' }} />
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 16, color: 'var(--blue)', letterSpacing: -0.5 }}>
              Hire<span style={{ color: 'var(--text)' }}>Kahani</span>
            </span>
          </div>
        </a>

        {/* Flex spacer */}
        <div style={{ flex: 1 }} />

        {/* Right actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Search icon toggle */}
          <button
            onClick={() => setSearchOpen(v => !v)}
            aria-label="Toggle search"
            style={{
              width: 40, height: 40, borderRadius: 10,
              background: searchOpen ? 'var(--surface2)' : 'transparent',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: searchOpen ? 'var(--blue)' : 'var(--text2)',
              transition: 'all 0.2s',
            }}
          >
            {searchOpen ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
            )}
          </button>

          {/* Notification Bell — signed-in users only */}
          {!user?.isAnonymous && <NotificationBell user={user} onReviewClick={onReviewClick} />}

          {/* Dark mode toggle */}
          <button
            onClick={toggleDarkMode}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'transparent', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text2)',
            }}
          >
            {darkMode ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>

        </div>
      </header>

      {/* Slide-down search bar */}
      {searchOpen && (
        <div className="mobile-search-bar open" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg
              width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="var(--text3)" strokeWidth="2.5"
              style={{ flexShrink: 0 }}
            >
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={searchValue}
              placeholder="Search company, role, city…"
              onChange={e => handleSearch(e.target.value)}
              style={{
                flex: 1, border: 'none', outline: 'none',
                fontSize: 16, fontFamily: 'var(--sans)',
                color: 'var(--text)', background: 'transparent',
              }}
            />
            {searchValue && (
              <button
                onClick={closeSearch}
                style={{
                  background: 'none', border: 'none',
                  color: 'var(--text3)', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, padding: '4px 8px',
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Navigation menu drawer */}
      {menuOpen && (
        <>
          <div
            onClick={() => setMenuOpen(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 840,
              background: 'rgba(0,0,0,0.3)',
            }}
          />
          <div style={{
            position: 'fixed', left: 0,
            top: 0, bottom: 0,
            width: '75vw', maxWidth: 280,
            zIndex: 850,
            background: 'var(--bg)',
            borderRadius: '0 18px 18px 0',
            boxShadow: '4px 0 32px rgba(0,0,0,0.15)',
            display: 'flex', flexDirection: 'column',
            animation: 'slideInLeft 0.25s ease-out',
            overflowY: 'auto',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 18px', borderBottom: '1px solid var(--border)',
              flexShrink: 0,
            }}>
              <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 16, color: 'var(--blue)' }}>
                Hire<span style={{ color: 'var(--text)' }}>Kahani</span>
              </span>
              <button
                onClick={() => setMenuOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', padding: 4 }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Nav items */}
            {ALL_NAV.map((item, idx) => {
              const isActive = activeView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  style={{
                    width: '100%', textAlign: 'left', padding: '13px 18px',
                    background: isActive ? 'rgba(14,165,233,0.06)' : 'transparent',
                    border: 'none', borderTop: idx === 0 ? 'none' : '1px solid var(--border)',
                    cursor: 'pointer', fontFamily: 'var(--sans)',
                    fontSize: 14, fontWeight: isActive ? 700 : 500,
                    color: isActive ? 'var(--blue)' : 'var(--text)',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}
                >
                  <span style={{ fontSize: 18 }}>{item.icon}</span>
                  {item.label}
                  {isActive && <span style={{ marginLeft: 'auto', width: 7, height: 7, borderRadius: '50%', background: 'var(--blue)', display: 'inline-block' }} />}
                </button>
              );
            })}

            {/* Profile button — only for logged-in users */}
            {user && !isGuest && (
              <button
                onClick={() => { setMenuOpen(false); navigate('/profile'); }}
                style={{
                  width: '100%', textAlign: 'left', padding: '13px 18px',
                  background: 'transparent',
                  border: 'none', borderTop: '1px solid var(--border)',
                  cursor: 'pointer', fontFamily: 'var(--sans)',
                  fontSize: 14, fontWeight: 500,
                  color: 'var(--text)',
                  display: 'flex', alignItems: 'center', gap: 12,
                }}
              >
                <span style={{ fontSize: 18 }}>👤</span>
                My Profile
              </button>
            )}

            {/* Sign Out / Login */}
            <div style={{ marginTop: 'auto', padding: '12px 10px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
              {user && !isGuest && (
                <div style={{ padding: '8px 18px 10px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: '50%',
                    background: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0,
                  }}>
                    {(user.displayName || user.hkId || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2 }}>
                      {user.displayName || user.hkId || ''}
                    </div>
                    {user.displayName && user.hkId && (
                      <div style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.2 }}>{user.hkId}</div>
                    )}
                  </div>
                </div>
              )}
              {user && !isGuest ? (
                <button
                  onClick={handleSignOut}
                  style={{
                    width: '100%', textAlign: 'left', padding: '13px 18px',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
                    color: '#ef4444',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}
                >
                  <span style={{ fontSize: 18 }}>🚪</span>
                  Sign Out
                </button>
              ) : (
                <button
                  onClick={() => { setMenuOpen(false); navigate('/login'); }}
                  style={{
                    width: '100%', textAlign: 'left', padding: '13px 18px',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
                    color: 'var(--blue)',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}
                >
                  <span style={{ fontSize: 18 }}>🔑</span>
                  Sign In
                </button>
              )}
            </div>
          </div>
          <style>{`@keyframes slideInLeft { from { transform: translateX(-100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
        </>
      )}
    </>
  );
}
